import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ShoppingCart, Eye, Check, X, Sparkles, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useCart } from "@/context/CartContext";
import { variantAvailable, firstAvailable, cartLabel } from "@/lib/merchStock";
import useShipping, { shippingLine } from "@/hooks/useShipping";
import Price, { saleCountdown } from "./Price";
import WishlistButton from "./WishlistButton";

function Chip({ label, active, ok, onClick, testId }) {
  return (
    <button type="button" data-testid={testId} disabled={!ok} onClick={onClick} title={ok ? label : `${label} — sold out`}
      className={`min-w-[38px] px-2 py-1 rounded-md border-2 text-[11px] font-bold transition-colors ${active ? "bg-[#0C2E2B] text-white border-[#0C2E2B]" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"} ${!ok ? "opacity-40 line-through cursor-not-allowed" : ""}`}>
      {label}
    </button>
  );
}

export default function MerchQuickCard({ p, idx, onDetails, bundle, onBundle }) {
  const { addItem, setOpen } = useCart();
  const ship = useShipping();
  const [picking, setPicking] = useState(false);
  const [sel, setSel] = useState(() => firstAvailable(p));
  const hasVariants = (p.sizes?.length || 0) + (p.colors?.length || 0) > 0;
  const totalAvail = variantAvailable(p, "", "");
  const soldOut = totalAvail <= 0;
  const avail = variantAvailable(p, sel.size, sel.color);
  const image = (p.variant_images && sel.color && p.variant_images[sel.color]) || p.image;

  const commit = () => {
    if (avail <= 0) return toast.error("That option is sold out");
    addItem({ ...p, image, price: p.effective_price ?? p.price }, cartLabel(sel.size, sel.color));
    toast.success(`Added ${p.title}${hasVariants ? ` (${cartLabel(sel.size, sel.color)})` : ""} to cart`);
    setPicking(false);
    setOpen(true);
  };

  const onAdd = (e) => {
    e.stopPropagation();
    if (soldOut) return;
    if (!hasVariants) return commit();
    setPicking((v) => !v);
  };

  return (
    <motion.article
      layout
      data-testid={`merch-card-${idx + 1}`}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.35, delay: Math.min(idx * 0.05, 0.4) }}
      whileHover={{ y: -6 }}
      className="bg-white rounded-2xl border-2 border-zinc-200 overflow-hidden group hover:border-[#0C2E2B] hover:shadow-[0_24px_50px_-28px_rgba(12,46,43,0.5)] transition-[border-color,box-shadow] duration-300 flex flex-col"
    >
      <button type="button" data-testid={`merch-card-image-${idx + 1}`} onClick={() => onDetails(p)} className="relative bg-zinc-100 overflow-hidden text-left">
        <img src={image} alt={p.title} className={`w-full h-64 object-cover transition-transform duration-700 group-hover:scale-105 ${soldOut ? "grayscale opacity-70" : ""}`} />
        <div className="absolute top-3 left-3 flex gap-1.5">
          {p.on_sale && <Badge data-testid={`merch-sale-badge-${idx + 1}`} className="bg-red-600 hover:bg-red-600 text-white text-[10px] font-bold">Sale · save ${(Number(p.original_price) - Number(p.effective_price)).toFixed(0)}</Badge>}
          {saleCountdown(p) && <Badge data-testid={`merch-sale-countdown-${idx + 1}`} className="bg-white/90 hover:bg-white text-red-700 border border-red-200 text-[10px] font-bold"><Clock className="w-3 h-3 mr-1" /> {saleCountdown(p)}</Badge>}
          {p.trending && <Badge className="bg-[#0C2E2B] hover:bg-[#0C2E2B] text-[#FFDF00] text-[10px] font-bold"><Sparkles className="w-3 h-3 mr-1" /> Trending</Badge>}
          {soldOut ? <Badge className="bg-zinc-800 hover:bg-zinc-800 text-white text-[10px] font-bold">Sold out</Badge>
            : totalAvail <= 5 && <Badge className="bg-[#FFDF00] hover:bg-[#FFDF00] text-[#0C2E2B] text-[10px] font-bold">Only {totalAvail} left</Badge>}
        </div>
        <div className="absolute top-3 right-3"><WishlistButton product={p} size={sel.size} color={sel.color} testId={`merch-wishlist-btn-${idx + 1}`} /></div>
        {bundle && (
          <span
            role="link"
            tabIndex={0}
            data-testid={`merch-bundle-ribbon-${idx + 1}`}
            onClick={(e) => { e.stopPropagation(); onBundle?.(bundle); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onBundle?.(bundle); } }}
            className="absolute -left-12 top-5 -rotate-45 w-48 py-1 bg-red-600 text-white text-[10px] font-black uppercase tracking-wider text-center shadow-md hover:bg-red-700 cursor-pointer"
            title={`Part of ${bundle.title} — save $${Number(bundle.savings).toFixed(2)}`}
          >
            Bundle deal · save ${Number(bundle.savings).toFixed(0)}
          </span>
        )}
      </button>
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="font-display font-black text-lg text-[#0C2E2B]">{p.title}</h3>
        <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{p.description}</p>
        <div className="mt-3 flex items-baseline justify-between">
          <Price p={p} className="text-xl" testId={`merch-price-${idx + 1}`} />
          <div className="text-[10px] text-zinc-500" data-testid={`merch-shipping-line-${idx + 1}`}>{shippingLine(ship)}</div>
        </div>

        <AnimatePresence initial={false}>
          {picking && (
            <motion.div key="picker" data-testid={`merch-quick-picker-${idx + 1}`} initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
              <div className="mt-3 rounded-lg bg-zinc-50 border border-zinc-200 p-3 space-y-2">
                {p.colors?.length > 0 && (
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1">Color · {sel.color}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {p.colors.map((c) => <Chip key={c} label={c} active={sel.color === c} ok={variantAvailable(p, "", c) > 0} onClick={() => setSel((s) => ({ ...s, color: c }))} testId={`merch-quick-color-${idx + 1}-${c}`} />)}
                    </div>
                  </div>
                )}
                {p.sizes?.length > 0 && (
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1">Size · {sel.size}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {p.sizes.map((s) => <Chip key={s} label={s} active={sel.size === s} ok={variantAvailable(p, s, sel.color) > 0} onClick={() => setSel((v) => ({ ...v, size: s }))} testId={`merch-quick-size-${idx + 1}-${s}`} />)}
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-between gap-2 pt-1">
                  <span className="text-[10px] text-zinc-500" data-testid={`merch-quick-avail-${idx + 1}`}>{avail <= 0 ? "Sold out in this option" : avail <= 5 ? `Only ${avail} left` : "In stock"}</span>
                  <div className="flex gap-1.5">
                    <Button type="button" size="sm" variant="outline" className="h-8 rounded-md" onClick={() => setPicking(false)} data-testid={`merch-quick-cancel-${idx + 1}`}><X className="w-3.5 h-3.5" /></Button>
                    <Button type="button" size="sm" data-testid={`merch-quick-confirm-${idx + 1}`} disabled={avail <= 0} onClick={commit} className="h-8 rounded-md btn-kinetic text-xs"><Check className="w-3.5 h-3.5 mr-1" /> Add</Button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <Button data-testid={`merch-add-to-cart-btn-${idx + 1}`} onClick={onAdd} disabled={soldOut} className="btn-kinetic rounded-md text-xs h-10 disabled:opacity-50">
            <ShoppingCart className="w-4 h-4 mr-1.5" /> {soldOut ? "Sold out" : picking ? "Choose option" : "Add to cart"}
          </Button>
          <Button data-testid={`merch-details-btn-${idx + 1}`} variant="outline" onClick={() => onDetails(p)} className="rounded-md text-xs h-10 border-[#0C2E2B] text-[#0C2E2B] font-bold">
            <Eye className="w-4 h-4 mr-1.5" /> See details
          </Button>
        </div>
      </div>
    </motion.article>
  );
}
